"use client"

import type React from "react"
import { useState, useEffect, useCallback } from "react"
import { Card, CardContent } from "@/components/ui/card"
import { Info } from "lucide-react"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { useTranslation } from "react-i18next"
import axiosInstance from "@/api/axios"
import { useTheme } from "@/pages/contexts/ThemeContext"

interface DateRangeType {
    startDate: Date | string | null
    endDate: Date | string | null
    key: string
}

interface WasteCreatedProps {
    bins: string[]
    timeRange: string
    onKgWasteUpdate?: (kg: number) => void
    customDateRange?: DateRangeType[]
    isCustomDateRange?: boolean
}

interface SeasonalWasteData {
    paper: number
    pmd: number
    residual: number
    organic: number
    total: number
    custom_date_range?: {
        start: string
        end: string
    }
}

interface EnvironmentalImpactData {
    total_co2_without_garby: number
    total_co2_with_garby: number
    co2_savings: number
    equivalents: {
        [key: string]: {
            value: number
            unit: string
            note: string
        }
    }
    period_start: string
    period_end: string
    custom_date_range?: {
        start: string
        end: string
    }
}

interface SelectedEquivalent {
    key: string
    value: number
    unit: string
    displayName: string
    tooltip: string
}

// Add debounce utility
const useDebounce = <T,>(value: T, delay: number): T => {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

// Helper function to safely convert to ISO date string
const toISODateString = (date: Date | string | null): string | null => {
    if (!date) return null;
    
    try {
        // If it's already a string, check if it's a valid ISO date
        if (typeof date === 'string') {
            // Try to parse it to validate
            const parsed = new Date(date);
            if (isNaN(parsed.getTime())) return null;
            // Return just the date part (YYYY-MM-DD)
            return date.split('T')[0];
        }
        
        // If it's a Date object
        if (date instanceof Date) {
            if (isNaN(date.getTime())) return null;
            return date.toISOString().split('T')[0];
        }
        
        return null;
    } catch (error) {
        console.error('Error converting date to ISO string:', error);
        return null;
    }
};

const WasteCreated: React.FC<WasteCreatedProps> = ({ 
    timeRange, 
    bins, 
    onKgWasteUpdate,
    customDateRange = [{
        startDate: null,
        endDate: null,
        key: 'selection'
    }],
    isCustomDateRange = false
}) => {
    const { t } = useTranslation()
    const { theme } = useTheme()
    const isDarkMode = theme === "dark"

    const [kgWaste, setKgWaste] = useState<number>(0)
    const [selectedEquivalent, setSelectedEquivalent] = useState<SelectedEquivalent | null>(null)
    const [loading, setLoading] = useState<boolean>(false)
    const [error, setError] = useState<string>("")

    // Create a unique key for debouncing - safely handle dates
    const startDateStr = toISODateString(customDateRange[0]?.startDate);
    const endDateStr = toISODateString(customDateRange[0]?.endDate);
    
    const fetchKey = `${timeRange}-${bins.join(',')}-${isCustomDateRange ? startDateStr : ''}-${isCustomDateRange ? endDateStr : ''}`
    
    // Debounce the fetch key
    const debouncedFetchKey = useDebounce(fetchKey, 1000)

    const getBackgroundColor = () => (isDarkMode ? "#100c0c" : "#ffffff")

    const equivalentDisplayMap: { [key: string]: { displayName: string; tooltip: string } } = {
        tree_sequestration: { displayName: t("trees"), tooltip: t("treeTooltip") },
        gasoline_consumed: { displayName: t("gallonsOfGasoline"), tooltip: t("gasolineTooltip") },
        average_car_drive: { displayName: t("milesOfCarDriving"), tooltip: t("carDriveTooltip") },
        water_bottles: { displayName: t("plasticWaterBottles"), tooltip: t("waterBottlesTooltip") },
        smartphone_charges: { displayName: t("smartphoneCharges"), tooltip: t("smartphoneChargesTooltip") },
        lorry_drive: { displayName: t("milesOfTruckDriving"), tooltip: t("lorryDriveTooltip") },
        home_electricity: { displayName: t("hoursOfHomeElectricity"), tooltip: t("homeElectricityTooltip") },
    }

    // Normalize timeRange for API call
    const normalizeTimeRangeForAPI = useCallback((range: string): string => {
        const cleaned = range.replace(/\s+/g, "").toLowerCase()
        const map: Record<string, string> = {
            day: "Day",
            week: "Week",
            month: "Month",
            "3months": "3Month",
            "3month": "3Month",
            "6months": "6Month",
            "6month": "6Month",
            year: "Year",
        }
        return map[cleaned] || "Month"
    }, [])

    const fetchData = useCallback(async () => {
        if (loading) return;

        try {
            setLoading(true)
            setError("")

            // Build params for waste composition API
            const wasteParams: any = {
                timeRange: normalizeTimeRangeForAPI(timeRange),
                bins: bins.join(","),
            }

            // Add custom date range if active
            if (isCustomDateRange && customDateRange[0]?.startDate && customDateRange[0]?.endDate) {
                const startDate = toISODateString(customDateRange[0].startDate);
                const endDate = toISODateString(customDateRange[0].endDate);
                
                if (startDate && endDate) {
                    wasteParams.start_date = startDate;
                    wasteParams.end_date = endDate;
                    console.log('🗓️ WasteCreated using custom dates:', startDate, 'to', endDate);
                } else {
                    console.error('Failed to convert dates to ISO string');
                }
            }

            // Fetch waste composition
            const wasteResponse = await axiosInstance.get("others_page_waste_composition", {
                params: wasteParams,
                paramsSerializer: { indexes: null },
            })

            const wasteData: SeasonalWasteData = wasteResponse.data
            const seasonWasteTotal = wasteData.paper + wasteData.pmd + wasteData.organic + wasteData.residual

            setKgWaste(seasonWasteTotal)

            if (onKgWasteUpdate) {
                onKgWasteUpdate(seasonWasteTotal)
            }

            // Build params for environmental impact API
            const currentYear = new Date().getFullYear()
            const impactParams: any = {
                period: normalizeTimeRangeForAPI(timeRange),
                year: currentYear.toString(),
                bins: bins.join(","),
            }

            // Add custom date range if active
            if (isCustomDateRange && customDateRange[0]?.startDate && customDateRange[0]?.endDate) {
                const startDate = toISODateString(customDateRange[0].startDate);
                const endDate = toISODateString(customDateRange[0].endDate);
                
                if (startDate && endDate) {
                    impactParams.start_date = startDate;
                    impactParams.end_date = endDate;
                }
            }

            const impactResponse = await axiosInstance.get("enviromental_impact", {
                params: impactParams,
                paramsSerializer: { indexes: null },
            })

            const impactData: EnvironmentalImpactData = impactResponse.data

            const meaningfulEquivalents = Object.entries(impactData.equivalents).filter(
                ([, equiv]) => equiv.value > 0
            )

            if (meaningfulEquivalents.length > 0) {
                const randomIndex = Math.floor(Math.random() * meaningfulEquivalents.length)
                const [key, equivalent] = meaningfulEquivalents[randomIndex]
                const displayInfo = equivalentDisplayMap[key] || {
                    displayName: equivalent.unit,
                    tooltip: equivalent.note,
                }

                setSelectedEquivalent({
                    key,
                    value: Math.round(equivalent.value),
                    unit: equivalent.unit,
                    displayName: displayInfo.displayName,
                    tooltip: displayInfo.tooltip,
                })
            } else {
                setSelectedEquivalent({
                    key: "fallback",
                    value: Math.round(seasonWasteTotal / 70),
                    unit: t("washingMachines"),
                    displayName: t("washingMachines"),
                    tooltip: t("washingMachinesTooltip"),
                })
            }
        } catch (err: any) {
            console.error("Error fetching data:", err)
            setError(t("wasteCreatedError"))
            setKgWaste(457.8)
            if (onKgWasteUpdate) onKgWasteUpdate(457.8)

            setSelectedEquivalent({
                key: "fallback",
                value: 6,
                unit: t("washingMachines"),
                displayName: t("washingMachines"),
                tooltip: t("environmentalImpactDefault"),
            })
        } finally {
            setLoading(false)
        }
    }, [timeRange, bins, isCustomDateRange, customDateRange, onKgWasteUpdate, normalizeTimeRangeForAPI, loading, t, equivalentDisplayMap])

    // CRITICAL FIX: Only fetch when debounced key changes
    useEffect(() => {
        console.log('🔄 WasteCreated fetch triggered with key:', debouncedFetchKey)
        fetchData()
    }, [debouncedFetchKey])

    const getComparisonText = (): string =>
        selectedEquivalent
            ? t("environmentalImpactComparable", {
                  value: selectedEquivalent.value.toLocaleString(),
                  displayName: selectedEquivalent.displayName,
              })
            : t("calculatingImpact")

    const getTooltipText = (): string => selectedEquivalent?.tooltip || t("environmentalImpactDefault")

    const cardClasses = `flex flex-col h-full`
    const cardStyle = { backgroundColor: getBackgroundColor() }
    const titleClasses = `text-base sm:text-lg font-medium mb-3 text-center ${isDarkMode ? "text-gray-100" : "text-gray-900"}`
    const textClasses = `text-xs leading-relaxed flex-1 break-words ${isDarkMode ? "text-gray-300" : "text-gray-600"}`
    const kgTextClasses = `text-base font-medium ${isDarkMode ? "text-gray-300" : "text-gray-600"}`
    const errorTextClasses = `text-sm px-4 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`
    const iconClasses = `h-3 w-3 cursor-pointer flex-shrink-0 mt-0.5 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`

    // Helper to format date for display
    const formatDateForDisplay = (date: Date | string | null): string => {
        if (!date) return '';
        try {
            const dateObj = typeof date === 'string' ? new Date(date) : date;
            if (isNaN(dateObj.getTime())) return '';
            return dateObj.toLocaleDateString();
        } catch {
            return '';
        }
    };

    if (loading) {
        return (
            <Card className={cardClasses} style={cardStyle}>
                <CardContent className="flex-1 flex flex-col p-4 sm:p-6">
                    <div className="flex justify-between items-center mb-3">
                        <h2 className={titleClasses}>{t("wasteCreatedTitle")}</h2>
                    </div>
                    <div className="flex-1 flex items-center justify-center">
                        <div
                            className={`animate-pulse w-20 h-20 rounded-full ${
                                isDarkMode ? "bg-gray-700" : "bg-gray-200"
                            }`}
                        ></div>
                    </div>
                </CardContent>
            </Card>
        )
    }

    if (error) {
        return (
            <Card className={cardClasses} style={cardStyle}>
                <CardContent className="flex-1 flex flex-col p-4 sm:p-6">
                    <div className="flex justify-between items-center mb-3">
                        <h2 className={titleClasses}>{t("wasteCreatedTitle")}</h2>
                    </div>
                    <div className="flex-1 flex flex-col items-center justify-center text-center space-y-4">
                        <p className="text-3xl font-bold text-red-500">{t("error")}</p>
                        <p className={errorTextClasses}>{error}</p>
                    </div>
                </CardContent>
            </Card>
        )
    }

    return (
        <Card className={cardClasses} style={cardStyle}>
            <CardContent className="flex-1 flex flex-col p-3 sm:p-4">
                {/* Header with date range display */}
                <div className="flex flex-col items-center mb-3">
                    <h2 className={titleClasses}>{t("wasteCreatedTitle")}</h2>
                    {isCustomDateRange && customDateRange[0]?.startDate && customDateRange[0]?.endDate && (
                        <p className="text-xs text-muted-foreground text-center mt-1">
                            {formatDateForDisplay(customDateRange[0].startDate)} - {formatDateForDisplay(customDateRange[0].endDate)}
                        </p>
                    )}
                </div>

                <div className="flex-1 flex flex-col justify-center items-center text-center space-y-3">
                    <div className="flex flex-col items-center space-y-1">
                        <p className="text-3xl sm:text-4xl font-bold text-green-500 leading-none">
                            {kgWaste.toFixed(2)}
                        </p>
                        <p className={kgTextClasses}>KG</p>
                    </div>

                    <div className="flex items-start justify-center space-x-2 max-w-full px-1">
                        <p className={textClasses}>{getComparisonText()}</p>
                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Info className={iconClasses} />
                                </TooltipTrigger>
                                <TooltipContent
                                    className={`text-xs font-medium max-w-64 ${
                                        isDarkMode ? "bg-gray-700 text-gray-200 border-gray-600" : "bg-white text-gray-900 border-gray-200"
                                    }`}
                                    side="bottom"
                                >
                                    {getTooltipText()}
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    </div>
                </div>
            </CardContent>
        </Card>
    )
}

export default WasteCreated